<template>
  <div class="wrapper inline-grid grid-cols-9 col-gap width-full">
    <header class="page-title col-span-6 col-start-2">
      <h1 class="header-one">
        Resize
      </h1>
      <p class="page-title__sub">
        Utility classes for controlling the resizing elements.
      </p>
    </header>
    <section class="section col-span-6 col-start-2">
      <div class="table">
        <table>
          <thead>
            <tr>
              <th>Class</th>
              <th>Properties</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code class="code__class">.resize</code>
              </td>
              <td>
                <code class="code__prop">resize: both;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.resize-none</code>
              </td>
              <td>
                <code class="code__prop">resize: none;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.resize-x</code>
              </td>
              <td>
                <code class="code__prop">resize: horizontal;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.resize-y</code>
              </td>
              <td>
                <code class="code__prop">resize: vertical;</code>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
    <section class="section col-span-6 col-start-2">
      <header class="section__title">
        <h2 class="header-two">
          All
        </h2>
      </header>
      <p>
        <code class="margin-left-0">.resize</code> is used when you want the element to
        be resizable, both horizontally and vertically.
      </p>
      <code-block>
        <template #code>
          <pre class="language-html" rel="html">
          <code>&lt;div class="field field--textarea..."&gt;
  &lt;label for="resize_all" class="label"&gt;
    ...
  &lt;/label&gt;
  &lt;textarea id="resize_all"&gt;
    &lt;/textarea&gt;
  &lt;/div&gt;
&lt;/div&gt;</code>
          </pre>
        </template>
      </code-block>
      <preview>
        <template #preview>
          <div class="bg-wrapper grid grid-cols-4 col-gap l-col-gap m-col-gap sm-col-gap
          align-items-stretch width-full bg-tiara grid-rows-3
          padding-top-30 padding-bottom-30 container l-container m-container s-container"
          >
            <div class="field field--textarea col-span-2 col-start-2 m-col-span-4 m-col-start-1
              flex flex-col margin-bottom-20"
            >
              <label for="resize_all"
                     class="margin-bottom-5"
              >
                <code class="code">.resize</code>
              </label>
              <textarea id="resize_all"
                        class="textarea resize"
                        rows="6"
              />
            </div>
          </div>
        </template>
      </preview>
    </section>
    <section class="section col-span-6 col-start-2">
      <header class="section__title">
        <h2 class="header-two">
          None
        </h2>
      </header>
      <p>
        <code class="margin-left-0">.resize-none</code> is used when you don't want the element to
        be resizable in any direction.
      </p>
      <code-block>
        <template #code>
          <pre class="language-html" rel="html">
          <code>&lt;div class="field field--textarea..."&gt;
  &lt;label for="resize_none" class="label"&gt;
    ...
  &lt;/label&gt;
  &lt;textarea id="resize_none"&gt;
    &lt;/textarea&gt;
  &lt;/div&gt;
&lt;/div&gt;</code>
          </pre>
        </template>
      </code-block>
      <preview>
        <template #preview>
          <div class="bg-wrapper grid grid-cols-4 col-gap l-col-gap m-col-gap sm-col-gap
          align-items-stretch width-full bg-tiara grid-rows-3
          padding-top-30 padding-bottom-30 container l-container m-container s-container"
          >
            <div class="field field--textarea col-span-2 col-start-2 m-col-span-4 m-col-start-1
              flex flex-col margin-bottom-20"
            >
              <label for="resize_none"
                     class="margin-bottom-5"
              >
                <code class="code">.resize-none</code>
              </label>
              <textarea id="resize_none"
                        class="textarea resize-none"
                        rows="6"
              />
            </div>
          </div>
        </template>
      </preview>
    </section>
    <section class="section col-span-6 col-start-2">
      <header class="section__title">
        <h2 class="header-two">
          Horizontal
        </h2>
      </header>
      <p>
        <code class="margin-left-0">.resize-x</code> is used when you only want the element to
        resize horizontally.
      </p>
      <code-block>
        <template #code>
          <pre class="language-html" rel="html">
          <code>&lt;div class="field field--textarea..."&gt;
  &lt;label for="resize_x" class="label"&gt;
    ...
  &lt;/label&gt;
  &lt;textarea id="resize_x"&gt;
    &lt;/textarea&gt;
  &lt;/div&gt;
&lt;/div&gt;</code>
          </pre>
        </template>
      </code-block>
      <preview>
        <template #preview>
          <div class="bg-wrapper grid grid-cols-4 col-gap l-col-gap m-col-gap sm-col-gap
          align-items-stretch width-full bg-tiara grid-rows-3
          padding-top-30 padding-bottom-30 container l-container m-container s-container"
          >
            <div class="field field--textarea col-span-2 col-start-2 m-col-span-4 m-col-start-1
              flex flex-col margin-bottom-20"
            >
              <label for="resize_x"
                     class="margin-bottom-5"
              >
                <code class="code">.resize-x</code>
              </label>
              <textarea id="resize_x"
                        class="textarea resize-x"
                        rows="6"
              />
            </div>
          </div>
        </template>
      </preview>
    </section>
    <section class="section col-span-6 col-start-2">
      <header class="section__title">
        <h2 class="header-two">
          Vertical
        </h2>
      </header>
      <p>
        <code class="margin-left-0">.resize-y</code> is used when you only want the element to
        resize vertically.
      </p>
      <code-block>
        <template #code>
          <pre class="language-html" rel="html">
          <code>&lt;div class="field field--textarea..."&gt;
  &lt;label for="resize_y" class="label"&gt;
    ...
  &lt;/label&gt;
  &lt;textarea id="resize_y"&gt;
    &lt;/textarea&gt;
  &lt;/div&gt;
&lt;/div&gt;</code>
          </pre>
        </template>
      </code-block>
      <preview>
        <template #preview>
          <div class="bg-wrapper grid grid-cols-4 col-gap l-col-gap m-col-gap sm-col-gap
          align-items-stretch width-full bg-tiara grid-rows-3
          padding-top-30 padding-bottom-30 container l-container m-container s-container"
          >
            <div class="field field--textarea col-span-2 col-start-2 m-col-span-4 m-col-start-1
              flex flex-col margin-bottom-20"
            >
              <label for="resize_y"
                     class="margin-bottom-5"
              >
                <code class="code">.resize-y</code>
              </label>
              <textarea id="resize_y"
                        class="textarea resize-y"
                        rows="6"
              />
            </div>
          </div>
        </template>
      </preview>
    </section>
  </div>
</template>

<script>
import Preview from '@/components/Preview';
import CodeBlock from '@/components/Code-Block';

export default {
  name: 'Resize',
  components: {
    Preview,
    CodeBlock,
  },
};
</script>
